import { Delete, Download, Edit } from '@mui/icons-material';
import type {
  GridEventListener,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { type JSX, type MutableRefObject, useCallback, useContext, useMemo, useState } from 'react';

import type { PreviewList } from '~/analytics/events';
import { UserContext } from '~/auth/userContext';
import type { QuickFilterProps } from '~/components/common/QuickFilterInput';
import { AIIcon } from '~/components/icons';
import { useFinalDecision } from '~/components/invoices/finalDecision/useFinalDecision';
import InvoiceDialog from '~/components/invoices/InvoiceDialog';
import { InvoicePreviewPanel } from '~/components/invoices/InvoicePreviewPanel';
import { StampedInvoiceDownloader } from '~/components/invoices/stampedInvoice/StampedInvoiceDownloader';
import { openInNewTab } from '~/components/rowPreview/RowPreviewPanel';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable, LiftAIToolbarProps } from '~/components/table';
import { useGridViews } from '~/components/table/views/useGridViews';
import { useDeleteEntityDialog } from '~/components/tickets';
import { mapInvoiceToFormValues } from '~/components/uploadDocument/InvoiceForm';
import useInvoice from '~/data/hooks/useInvoice';
import { GLOBAL_FILTER_ITEM_IDS } from '~/data/hooks/useSpending';
import {
  type Invoice,
  InvoiceKind,
  type InvoiceWithPortfolio,
  isCredit,
  isInvoiceAutoUpload,
  isInvoiceFinal,
} from '~/data/schemas/invoice';
import { useGridExporter } from '~/hooks/useGridExporter';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { useOpenInvoice } from '~/hooks/useOpenInvoice';
import { useFeatureFlags } from '~/utils/featureFlags';
import { ACTIONS_ON_SPENDING, canAccess, PROPERTY_LINKS_IN_SPENDING } from '~/utils/role';

import { InvoiceDocumentFromList } from './InvoiceDocumentFromList';
import { getSpendingTableColumns } from './spendingTableColumns';

const defaultSortModel: GridSortModel = [{ field: 'date', sort: 'desc' }];

export type SpendingTableProps = {
  invoices: InvoiceWithPortfolio[];
  numInvoices: number;
  isLoading: boolean;
  onExport: () => Promise<InvoiceWithPortfolio[]>;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onFilterModelChange: (model: GridFilterModel) => void;
  paginationModel: GridPaginationModel;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  hiddenColumns?: string[];
  /**
   * Which set of saved views this table reads and writes. A mount point showing a narrower
   * table passes its own, or one list's saved view would rearrange the other.
   */
  viewsStoreKey?: string;
  enableQuickFilter?: boolean;
  quickFilterProps?: QuickFilterProps;
  canFilterProperty?: boolean;
  /**
   * Where a row leads, for a caller that wants the invoice opened in a new tab
   * rather than in place. Omit — as the Spending page itself does — and a row
   * opens the invoice over the current page through the drawer flow.
   */
  rowHref?: (row: InvoiceWithPortfolio) => string;
  /** Optional external grid apiRef. When provided, the parent can drive the
   * grid imperatively (e.g. scroll a column into view). Falls back to an
   * internal ref when omitted. */
  apiRef?: MutableRefObject<GridApiPremium>;
  /** Bounds the grid to this height so it scrolls internally instead of growing the page. */
  height?: number | string;
  enableRowPreview?: boolean;
  /** Names the list in analytics. A table inside a detail page passes its own. */
  previewList?: PreviewList;
};

export function SpendingTable({
  invoices,
  numInvoices,
  isLoading,
  onExport,
  onPaginationModelChange,
  onSortModelChange,
  onFilterModelChange,
  paginationModel,
  filterModel,
  sortModel,
  hiddenColumns = [],
  viewsStoreKey = 'invoices-table-columns-visibility',
  enableQuickFilter = false,
  quickFilterProps,
  canFilterProperty = true,
  rowHref,
  apiRef: externalApiRef,
  height,
  enableRowPreview = false,
  previewList = 'spending',
}: SpendingTableProps) {
  const [gridAction, setGridAction] = useState<{
    actionName: 'actionEdit' | 'actionDelete' | 'actionEditDecision';
    invoiceId: string;
  } | null>(null);
  const clearGridAction = useCallback(() => {
    setGridAction(null);
  }, [setGridAction]);
  const [pendingDownload, setPendingDownload] = useState<{
    invoice: Invoice;
    includeSupportingData: boolean;
  } | null>(null);
  const openInvoice = useOpenInvoice();

  const { showEntityActionSnackbar } = useLAISnackbar();
  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalApiRef;

  const featureFlags = useFeatureFlags();
  const showDisputeManagement = featureFlags.disputeManagement === true;
  const preview = useTableRowPreview({
    list: previewList,
    apiRef,
    scope: previewList === 'spending' ? 'page' : 'table',
    enabled: enableRowPreview,
  });
  const { rowPreview, enabled: rowPreviewEnabled, peekSwitch } = preview;
  const { openDocument } = rowPreview;
  const {
    currencyCode,
    userDetails: { role: userRole },
  } = useContext(UserContext);
  const terms = useLocaleTerms();

  const { invoice: invoiceToEdit, deleteInvoice } = useInvoice(
    gridAction?.actionName === 'actionDelete' || gridAction?.actionName === 'actionEdit'
      ? gridAction.invoiceId
      : null,
  );
  const invoiceFormData = useMemo(
    () => (invoiceToEdit ? mapInvoiceToFormValues(invoiceToEdit) : null),
    [invoiceToEdit],
  );
  const finalDecisionForm = useFinalDecision(
    gridAction?.actionName === 'actionEditDecision' ? gridAction.invoiceId : null,
    () => setGridAction(null),
  );

  const getFilename = useCallback(() => {
    return `liftai-spending-${new Date().toISOString()}`;
  }, []);
  const { GridToolbarExport } = useGridExporter<InvoiceWithPortfolio>({
    fetcher: onExport,
    apiRef,
    getFilename,
    onStartExport() {
      showEntityActionSnackbar(
        {
          name: 'Exporting',
          action: ActionEnum.StatusChange,
          id: 'Invoices',
        },
        {
          variant: 'info',
          persist: true,
        },
      );
    },
    onFinishedExport() {
      showEntityActionSnackbar(
        {
          name: 'Exported',
          action: ActionEnum.StatusChange,
          id: 'Invoices',
        },
        {
          variant: 'success',
          autoHideDuration: 5000,
        },
      );
    },
    onErrorExport() {
      showEntityActionSnackbar(
        {
          name: 'Exporting',
          action: ActionEnum.Fail,
          id: 'Invoices',
        },
        {
          variant: 'error',
          autoHideDuration: 5000,
        },
      );
    },
  });

  const { columnVisibilityModel, views, resetFilters, globalFilterItemIds } = useGridViews({
    apiRef,
    storeKey: viewsStoreKey,
    initialVisibility: {
      ...Object.fromEntries(hiddenColumns.map((col) => [col, false])),
    },
    externalFilterItemIds: GLOBAL_FILTER_ITEM_IDS,
  });

  const { dialog: deleteDialog, openForEntity: openDeleteDialogForInvoice } =
    useDeleteEntityDialog<Invoice>({
      onDelete: async (invoice) => {
        await deleteInvoice(invoice.id);

        showEntityActionSnackbar(
          {
            name: invoice.kind,
            action: ActionEnum.Delete,
            id: invoice.number,
          },
          {
            variant: 'info',
          },
        );
      },
      entityName: 'Invoice',
    });

  const onRowClick: GridEventListener<'rowClick'> = useCallback(
    ({ row }) => {
      openInvoice(row.id);
    },
    [openInvoice],
  );

  const spendingTableCols = useMemo(() => {
    const columnActions = (invoice: Invoice): JSX.Element[] => {
      const actions: JSX.Element[] = [];

      if (isInvoiceAutoUpload(invoice)) {
        actions.push(
          <GridActionsCellItem
            key="processed by AI"
            title="This invoice was processed by AI and may require verification. Please review for accuracy."
            label="processed by AI"
            icon={<AIIcon />}
            disabled
            // pointerEvents allow the tooltip to show on hover
            style={{ padding: 0, pointerEvents: 'all' }}
          />,
        );
      }

      // Only show edit and delete actions if user has permission
      if (canAccess(userRole, ACTIONS_ON_SPENDING)) {
        // Credits are filed approved but never stamped, so they edit like a draft.
        if (isCredit(invoice) || !isInvoiceFinal(invoice)) {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              onClick={() => setGridAction({ actionName: 'actionEdit', invoiceId: invoice.id })}
              label="Edit"
              showInMenu
            />,
          );
        } else {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              onClick={() =>
                setGridAction({ actionName: 'actionEditDecision', invoiceId: invoice.id })
              }
              label="Edit Stamped Invoice"
              showInMenu
            />,
          );
        }

        actions.push(
          <GridActionsCellItem
            key="delete"
            showInMenu
            icon={<Delete />}
            label="Delete"
            onClick={() => openDeleteDialogForInvoice(invoice)}
          />,
        );
      }

      if (isInvoiceFinal(invoice) && !isCredit(invoice)) {
        actions.push(
          <GridActionsCellItem
            key="download"
            showInMenu
            icon={<Download />}
            label={
              invoice.kind === InvoiceKind.Proposal
                ? 'Download Stamped Proposal'
                : 'Download Stamped Invoice'
            }
            onClick={() => setPendingDownload({ invoice, includeSupportingData: false })}
          />,
          <GridActionsCellItem
            key="download-supporting"
            showInMenu
            icon={<Download />}
            label="Download with Supporting Data"
            onClick={() => setPendingDownload({ invoice, includeSupportingData: true })}
          />,
        );
      }

      return actions;
    };

    const columns = getSpendingTableColumns({
      currencyCode,
      additionalColumns: [
        {
          field: 'actions',
          type: 'actions',
          getActions: ({ row: invoice }) => columnActions(invoice),
          width: 70,
          align: 'right',
        },
      ],
      canFilterProperty,
      canLinkToProperty: canAccess(userRole, PROPERTY_LINKS_IN_SPENDING),
      terms,
      showDisputeManagement,
      onPreviewDocument: rowPreviewEnabled ? (row) => openDocument(row.id) : undefined,
    });

    return columns;
  }, [
    rowPreviewEnabled,
    openDocument,
    currencyCode,
    openDeleteDialogForInvoice,
    canFilterProperty,
    userRole,
    terms,
    showDisputeManagement,
    setGridAction,
    setPendingDownload,
  ]);

  const initialState = {
    pinnedColumns: { right: ['actions'] },
    columns: {
      columnVisibilityModel,
    },
    sorting: {
      sortModel: defaultSortModel,
    },
  };

  const toolbarProps: LiftAIToolbarProps = useMemo(
    () => ({
      CustomGridToolbarExport: GridToolbarExport,
      views,
      resetFilters,
      globalFilterItemIds,
      filterControls: peekSwitch,
    }),
    [GridToolbarExport, views, resetFilters, globalFilterItemIds, peekSwitch],
  );

  return (
    <>
      <LiftAITable<InvoiceWithPortfolio>
        rows={invoices}
        apiRef={apiRef}
        rowCount={numInvoices}
        loading={isLoading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        filterMode="server"
        filterModel={filterModel}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        onFilterModelChange={onFilterModelChange}
        columns={spendingTableCols}
        tableHeaderTitle={'Spendings'}
        initialState={initialState}
        enableQuickFilter={enableQuickFilter}
        quickFilterProps={quickFilterProps}
        rowHref={rowHref}
        onRowClick={rowHref ? undefined : onRowClick}
        {...preview.tableProps}
        toolbarProps={toolbarProps}
        height={height}
      />
      {rowPreviewEnabled ? (
        <InvoicePreviewPanel
          rowPreview={rowPreview}
          onOpenFullPage={(id) => {
            const row = rowHref && invoices.find((invoice) => invoice.id === id);
            if (row) openInNewTab(rowHref(row));
            else openInvoice(id);
          }}
        />
      ) : null}
      {rowPreview.documentId ? (
        <InvoiceDocumentFromList
          invoiceId={rowPreview.documentId}
          onClose={rowPreview.closeDocument}
        />
      ) : null}
      {invoiceFormData ? (
        <InvoiceDialog
          open
          onClose={clearGridAction}
          onSubmit={clearGridAction}
          isEdit
          kind={invoiceFormData.kind}
          defaultValues={invoiceFormData}
        />
      ) : null}
      {deleteDialog}
      {gridAction?.actionName === 'actionEditDecision' ? finalDecisionForm : null}
      {pendingDownload ? (
        <StampedInvoiceDownloader
          invoice={pendingDownload.invoice}
          includeSupportingData={pendingDownload.includeSupportingData}
          onComplete={() => setPendingDownload(null)}
        />
      ) : null}
    </>
  );
}
